import React, {memo, ReactNode, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {
  ClickEventHandler,
  FocusEventHandler,
  MouseEventHandler,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {Skeleton} from '../../../../components/common/skeleton';

/*
 * Props.
 */

interface ReportListItemProps {
  isLoading: boolean;
  label?: ReactNode;
  value?: ReactNode;
  formattedVariation?: ReactNode;
  labelCircleColor?: string;
  icon?: IconName;
  color?: string;
  isHovered?: boolean;
  // Events
  onClick?: ClickEventHandler;
  onFocus?: FocusEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
}

/*
 * Styles.
 */

const StyledListItemWrapper = styled.div<{$shouldDisableHover?: boolean; $isHovered?: boolean}>`
  height: 40px;

  display: grid;
  grid-template-columns: 1fr auto 12px minmax(60px, auto);
  grid-template-areas: 'label value . variation';
  align-items: center;

  margin: 0 -12px;
  padding: 0 12px;

  border-radius: 12px;

  ${(p) =>
    !p.$shouldDisableHover &&
    p.$isHovered &&
    css`
      background-color: ${p.theme.greys.shade10};
    `}
`;

const StyledListItemLabel = styled.div`
  grid-area: label;
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
`;

const StlyedLabelColor = styled.div<{$color: string}>`
  border-radius: 50%;
  background-color: ${(p) => p.$color};
  width: 8px;
  height: 8px;
  margin-right: 8px;
`;

const StyledListItemValue = styled.span`
  grid-area: value;
  text-align: right;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledListItemVariationWrapper = styled.div<{$color: string}>`
  grid-area: variation;
  ${flexCenter}
  justify-content: space-between;
  color: ${(p) => p.$color};
`;

/*
 * Component.
 */

export const ReportListItem = memo<ReportListItemProps>(
  ({
    isLoading,
    label,
    value,
    formattedVariation,
    labelCircleColor,
    icon,
    color,
    isHovered,
    onClick,
    onFocus,
    onMouseEnter,
    onMouseLeave,
  }) => {
    const loadingBarWidth = useMemo(() => Math.max(20, Math.ceil(100 * Math.random())), []);

    if (isLoading) {
      return renderLoadingState(loadingBarWidth);
    }

    return (
      <StyledListItemWrapper
        $isHovered={isHovered}
        onClick={(e) => onClick && onClick(e)}
        onFocus={(e) => onFocus && onFocus(e)}
        onMouseEnter={(e) => onMouseEnter && onMouseEnter(e)}
        onMouseLeave={(e) => onMouseLeave && onMouseLeave(e)}
      >
        <StyledListItemLabel>
          {labelCircleColor && <StlyedLabelColor $color={labelCircleColor} />}
          {label}
        </StyledListItemLabel>
        <StyledListItemValue>{value}</StyledListItemValue>
        {icon && color && (
          <StyledListItemVariationWrapper $color={color}>
            <Icon name={icon} color={color} />
            {formattedVariation && formattedVariation}
          </StyledListItemVariationWrapper>
        )}
      </StyledListItemWrapper>
    );
  },
);

/*
 * Helpers.
 */

const renderLoadingState = (loadingBarWidth: number) => (
  <StyledListItemWrapper $shouldDisableHover>
    <StyledListItemLabel>
      <Skeleton width={`${loadingBarWidth}%`} />
    </StyledListItemLabel>
  </StyledListItemWrapper>
);
